<template>
  <KekkleBottomSheet
    v-model:open="isOpen"
    shadow="red"
    padding="normal"
    initial-height="full"
  >
    <div class="dark:text-slate-200">
      <!-- Header -->
      <div class="mb-6 flex items-start justify-between gap-4">
        <h2 class="flex-1 text-2xl font-bold">{{ $t("pages.quotes.edit") }}</h2>
        <default-button
          color="blue"
          :disabled="!validated || status.deleting"
          :loading="status.updating"
          @click="update"
        >
          {{ $t("global.inputs.labels.update") }}
        </default-button>
      </div>

      <!-- Form -->
      <div class="mb-4 w-full">
        <text-area-input
          v-model="quote"
          :has-error="validationErrors.quote"
          :error-message="$t('global.inputs.errors.quote_mandatory')"
          :label="$t('global.inputs.labels.quote') + '*'"
          :placeholder="$t('global.inputs.placeholders.quote')"
        />
      </div>

      <div class="mb-4 w-full">
        <p class="font-medium dark:text-slate-200">
          {{ $t("pages.quotes.who_said_it") }}
        </p>
        <p v-if="validationErrors.authors" class="mt-1 text-sm text-red-600">
          {{ $t("pages.quotes.one_author_minimum") }}
        </p>
        <multi-selector
          v-model="authors"
          label-key="name"
          index-key="uuid"
          :options="groupMemberOptions"
        />
      </div>

      <div class="mb-4 w-full">
        <text-input
          v-model="customAuthor"
          :label="$t('global.inputs.labels.quote_custom_author')"
          :placeholder="$t('global.inputs.placeholders.quote_custom_author')"
        />
      </div>

      <div class="mb-4 w-full">
        <text-area-input
          v-model="context"
          :has-error="validationErrors.description"
          error-message=""
          :label="$t('global.inputs.labels.quote_additional_context')"
          :placeholder="
            $t('global.inputs.placeholders.quote_additional_context')
          "
        />
      </div>

      <div class="mb-4 w-full">
        <p class="font-medium dark:text-slate-200">
          {{ $t("global.inputs.labels.date_time") }} *
        </p>
        <p
          v-show="validationErrors.timestamp"
          class="mt-1 text-sm text-red-600"
        >
          {{ $t("global.inputs.errors.date_time") }}
        </p>
        <div class="flex w-full flex-col items-center">
          <date-input v-model="timestamp" :date-time="true" />
        </div>
      </div>

      <!-- Delete Section -->
      <div v-if="!showDeleteConfirmation" class="mb-4 w-full">
        <default-button
          color="red"
          :disabled="status.updating"
          :loading="status.deleting"
          @click="showDeleteConfirmation = true"
        >
          <p>{{ $t("global.inputs.labels.delete_quote") }}</p>
        </default-button>
      </div>

      <!-- Inline Delete Confirmation -->
      <inline-delete-confirmation
        v-else
        color="red"
        :confirm-text="$t('pages.quotes.confirm_delete_text')"
        :cancel-text="$t('pages.quotes.confirm_close')"
        :delete-text="$t('pages.quotes.confirm_delete')"
        :loading="status.deleting"
        @cancel="showDeleteConfirmation = false"
        @confirm="deleteQuote"
      />
    </div>
  </KekkleBottomSheet>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
  setSuccessToast,
  setWarningToast,
} from "@/helpers/notifications/toast";
import TextInput from "@/components/Inputs/TextInput.vue";
import TextAreaInput from "@/components/Inputs/TextAreaInput.vue";
import MultiSelector from "@/components/Inputs/MultiSelector.vue";
import DefaultButton from "@/components/Buttons/DefaultButton.vue";
import DateInput from "@/components/Inputs/DateInput.vue";
import KekkleBottomSheet from "@/components/ui/KekkleBottomSheet.vue";
import InlineDeleteConfirmation from "@/components/InlineDeleteConfirmation.vue";
import { dateObjectToDateString } from "@/helpers/converts/date-converter";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useQuotesStore } from "@/stores/modules/quotes/quotes";

type QuoteData = {
  _id: string;
  quote: string;
  timestamp: Date;
  authors?: string[];
  custom_author?: string;
  context?: string;
};

type Props = {
  quoteData: QuoteData | null;
};

const props = defineProps<Props>();
const { t } = useI18n();
const groupsStore = useGroupsStore();
const quotesStore = useQuotesStore();

const isOpen = defineModel<boolean>("open", { required: true });

// Form state
const status = ref({
  updating: false,
  deleting: false,
});
const context = ref<string | undefined>(undefined);
const quote = ref<string | undefined>(undefined);
const timestamp = ref<string | undefined>(undefined);
const customAuthor = ref<string | undefined>(undefined);
const authors = ref<string[]>([]);
const showDeleteConfirmation = ref(false);
const quoteId = ref<string | null>(null);

// Computed properties
const groupData = computed(() => groupsStore.groupData);

const groupMembers = computed(() =>
  groupData.value?.members ? groupData.value.members : {},
);

const groupMemberOptions = computed(() => {
  const options: Array<{ uuid: string; name: string }> = [];
  for (const [key, value] of Object.entries(groupMembers.value)) {
    const memberObject = {
      uuid: key,
      name: (value as any).name
        ? (value as any).name
        : t("global.deleted_user"),
    };
    options.push(memberObject);
  }
  return options;
});

const timestampValid = computed(() => !!timestamp.value);

const quoteValid = computed(() => {
  return quote.value && quote.value.length > 0 && quote.value.length < 350;
});

const contextValid = computed(() => {
  if (context.value) {
    return context.value.length < 350;
  }
  return true;
});

const authorsValid = computed(() => {
  if (authors.value.length === 0 && !customAuthor.value) {
    return false;
  } else if (authors.value.length === 0 && customAuthor.value) {
    return customAuthor.value.length > 1 && customAuthor.value.length < 350;
  }
  return true;
});

const validationErrors = computed(() => ({
  quote: !quoteValid.value,
  context: !contextValid.value,
  timestamp: !timestampValid.value,
  authors: !authorsValid.value,
  description: false,
}));

const validated = computed(() => {
  return !Object.values(validationErrors.value).some((value) => value === true);
});

// Methods
function loadQuoteData(data: QuoteData) {
  if (!data) return;

  quoteId.value = data._id;
  context.value = data.context || undefined;
  quote.value = data.quote;
  timestamp.value = dateObjectToDateString(data.timestamp, "yyyy-mm-ddThh:mm");
  customAuthor.value = data.custom_author || undefined;

  // Ensure authors is an array and create a copy
  authors.value = Array.isArray(data.authors) ? [...data.authors] : [];
}

async function update() {
  status.value.updating = true;

  if (!validated.value) {
    setWarningToast({
      message: t("global.toast.check_all_fields"),
    });
    status.value.updating = false;
    return;
  }

  try {
    await quotesStore.updateQuote(quoteId.value!, {
      quote: quote.value!,
      timestamp: new Date(timestamp.value!),
      authors: authors.value ? authors.value : [],
      custom_author: customAuthor.value ? customAuthor.value : "",
      context: context.value ? context.value : "",
    });

    setSuccessToast({
      message: t("global.inputs.labels.quote_edited"),
      emoji: "💬",
    });
    isOpen.value = false;
  } catch (e) {
    console.error("Error updating quote:", e);
  } finally {
    status.value.updating = false;
  }
}

async function deleteQuote() {
  try {
    status.value.deleting = true;

    await quotesStore.deleteQuote(quoteId.value!);

    setSuccessToast({
      message: t("global.toast.deleted_successfully"),
      emoji: "🗑️",
    });
    isOpen.value = false;
  } catch (e) {
    console.error("Error deleting quote:", e);
  } finally {
    status.value.deleting = false;
  }
}

// Watchers
watch(isOpen, (newValue) => {
  if (!newValue) {
    // Reset delete confirmation when closing
    showDeleteConfirmation.value = false;
  } else if (newValue && props.quoteData) {
    // Load data when opening
    loadQuoteData(props.quoteData);
  }
});

watch(
  () => props.quoteData,
  (newData) => {
    if (newData && isOpen.value) {
      loadQuoteData(newData);
    }
  },
  { immediate: true },
);
</script>
